import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, RefreshCw, Save, X, Download, Power, PowerOff, Key, Eye, EyeOff, Shield, Settings } from 'lucide-react';
import { User, CreateUserRequest, UserRoleType } from '../interfaces/User';
import { useUser } from '../contexts/UserContext';
import { useAuth } from '../contexts/AuthContext';
import UserFilters from '../components/UserFilters';
import UserBulkActions from '../components/UserBulkActions';
import PermissionManager from '../components/PermissionManager';
import { DefaultLoader } from '../components';

const Users: React.FC = () => {
  const { state, actions } = useUser();
  const { hasPermission } = useAuth();
  const { loading, error } = state;
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [resetPasswordModal, setResetPasswordModal] = useState<{
    show: boolean;
    user?: User;
    tempPassword?: string;
  }>({ show: false });
  const [permissionModal, setPermissionModal] = useState<{
    show: boolean;
    user?: User;
    mode: 'user' | 'role';
    role?: string;
  }>({ show: false, mode: 'user' });
  
  const [formData, setFormData] = useState<CreateUserRequest>({
    username: '',
    lastname: '',
    email: '',
    password: '',
    role: 'emissor',
    is_active: true,
  });

  const roleOptions = [
    { value: 'admin', label: 'Administrador' },
    { value: 'gestor', label: 'Gestor' },
    { value: 'emissor', label: 'Emissor' },
    { value: 'reader', label: 'Leitor' },
  ];

  const canWrite = hasPermission('users.write');
  const canDelete = hasPermission('users.delete');

  useEffect(() => {
    actions.loadUsers();
  }, []);

  const filteredUsers = actions.getFilteredUsers();
  const stats = actions.getUserStats();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingUser) {
        const { password, ...updateData } = formData;
        await actions.updateUser(editingUser.id, updateData);
      } else {
        await actions.createUser(formData);
      }
      
      handleCancelForm();
    } catch (err: any) {
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      lastname: user.lastname,
      email: user.email,
      password: '',
      role: user.role,
      is_active: user.is_active,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este usuário?')) {
      return;
    }
    
    try {
      await actions.deleteUser(id);
    } catch (err: any) {
    }
  };

  const handleActivateToggle = async (user: User) => {
    try {
      if (user.is_active) {
        await actions.deactivateUser(user.id);
      } else {
        await actions.activateUser(user.id);
      }
    } catch (err: any) {
    }
  };

  const handleResetPassword = async (user: User) => {
    if (!window.confirm(`Tem certeza que deseja resetar a senha do usuário ${user.username}?`)) {
      return;
    }

    try {
      const result = await actions.resetUserPassword(user.id);
      setResetPasswordModal({
        show: true,
        user,
        tempPassword: result.temporary_password
      });
    } catch (err: any) {
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingUser(null);
    setFormData({
      username: '',
      lastname: '',
      email: '',
      password: '',
      role: 'emissor',
      is_active: true,
    });
    setShowPassword(false);
  };

  const handleExport = () => {
    const csvContent = [
      'Nome,Sobrenome,Email,Role,Status,Criado por,Data de Criação,Último Login',
      ...filteredUsers.map(user => [
        user.username,
        user.lastname,
        user.email,
        getRoleLabel(user.role),
        user.is_active ? 'Ativo' : 'Inativo',
        user.created_by,
        new Date(user.createdAt).toLocaleDateString('pt-BR'),
        user.last_login ? new Date(user.last_login).toLocaleDateString('pt-BR') : 'Nunca'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `usuarios-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const getRoleLabel = (role: UserRoleType) => {
    const option = roleOptions.find(opt => opt.value === role);
    return option?.label || role;
  };

  const formatDate = (dateString: string) => {
    console.log("o que chega aqui na datestring", dateString)
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo' 
  });
};

  if (loading) {
    return <DefaultLoader title="Usuários" messages={['Carregando usuários...', 'Processando permissões...', 'Organizando dados...']} />;
  }

  return (
    <div>
      <div className="d-flex justify-between align-center mb-4">
        <div>
          <h2 style={{ 
            fontSize: 'var(--font-size-24)', 
            fontWeight: 600, 
            margin: '0 0 8px 0' 
          }}>
            Gestão de Usuários
          </h2>
          <p style={{ 
            fontSize: 'var(--font-size-16)', 
            color: 'var(--color-darkgray)', 
            margin: 0 
          }}>
            Configure usuários e suas permissões no sistema
          </p>
        </div>
        
        <div className="d-flex gap-2">
          <button onClick={handleExport} className="btn btn-secondary" disabled={loading || filteredUsers.length === 0}>
            <Download size={16} />
            Exportar CSV
          </button>
          <button onClick={() => actions.loadUsers()} className="btn btn-secondary" disabled={loading}>
            <RefreshCw size={16} />
            Atualizar
          </button>
          <button 
            onClick={() => setPermissionModal({
              show: true,
              mode: 'role',
              role: 'admin'
            })} 
            className="btn btn-info"
          >
            <Shield size={16} />
            Gerenciar Roles
          </button>
          {canWrite && (
            <button onClick={() => setShowForm(true)} className="btn btn-primary">
              <Plus size={16} />
              Novo Usuário
            </button>
          )}
        </div>
      </div>

      {error && (
        <div style={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          color: 'var(--color-red)',
          padding: '12px 16px',
          borderRadius: 'var(--br-5)',
          marginBottom: '20px',
        }}>
          {error}
        </div>
      )}

      <UserFilters />

      {canWrite && (
        <UserBulkActions 
          selectedIds={selectedIds} 
          onSelectionChange={setSelectedIds}
        />
      )}

      {showForm && canWrite && (
        <div className="card mb-4">
          <h3 style={{ margin: '0 0 20px 0' }}>
            {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
          </h3>
          
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div className="form-group">
                <label className="form-label">Nome de Usuário</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="form-input"
                  placeholder="Ex: joao.silva"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Sobrenome</label>
                <input
                  type="text"
                  value={formData.lastname}
                  onChange={(e) => setFormData({ ...formData, lastname: e.target.value })}
                  className="form-input"
                  placeholder="Ex: Silva"
                  required
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="form-input"
                  placeholder="Ex: joao.silva@empresa.com"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRoleType })}
                  className="form-select"
                  required
                >
                  {roleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {!editingUser && (
              <div className="form-group mb-3">
                <label className="form-label">Senha</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="form-input"
                    placeholder="Digite a senha"
                    required={!editingUser}
                    style={{ paddingRight: '40px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--color-darkgray)'
                    }}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            )}

            <div className="form-group mb-3">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                Usuário ativo
              </label>
            </div>

            <div className="d-flex gap-2">
              <button type="submit" className="btn btn-primary">
                <Save size={16} />
                Salvar
              </button>
              <button type="button" onClick={handleCancelForm} className="btn btn-secondary">
                <X size={16} />
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              {canWrite && (
                <th style={{ width: '40px' }}>
                  <input
                    type="checkbox"
                    checked={selectedIds.length === filteredUsers.length && filteredUsers.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedIds(filteredUsers.map(u => u.id));
                      } else {
                        setSelectedIds([]);
                      }
                    }}
                  />
                </th>
              )}
              <th>Usuário</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Último Login</th>
              <th>Criado em</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={canWrite ? 8 : 7} className="text-center" style={{ padding: '40px' }}>
                  {state.filters.search || state.filters.status !== 'all' || state.filters.role !== 'all'
                    ? 'Nenhum usuário encontrado com os filtros aplicados'
                    : 'Nenhum usuário cadastrado'}
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => (
                <tr key={user.id}>
                  {canWrite && (
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(user.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedIds([...selectedIds, user.id]);
                          } else {
                            setSelectedIds(selectedIds.filter(id => id !== user.id));
                          }
                        }}
                      />
                    </td>
                  )}
                  <td>
                    <div>
                      <div style={{ fontWeight: 500 }}>{user.username}</div>
                      <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>
                        {user.lastname}
                      </div>
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`badge ${
                      user.role === 'admin' ? 'badge-danger' :
                      user.role === 'gestor' ? 'badge-warning' : 'badge-info'
                    }`}>
                      {getRoleLabel(user.role)}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${user.is_active ? 'badge-completed' : 'badge-cancelled'}`}>
                      {user.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td style={{ fontSize: 'var(--font-size-12)' }}>
                    {user.last_login ? formatDate(user.last_login) : 'Nunca'}
                  </td>
                  <td style={{ fontSize: 'var(--font-size-12)' }}>
                    {formatDate(user.createdAt)}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {canWrite && (
                        <>
                          <button
                            onClick={() => handleActivateToggle(user)}
                            className={`btn ${user.is_active ? 'btn-danger' : 'btn-success'}`}
                            style={{ padding: '6px 12px' }}
                            title={user.is_active ? 'Desativar' : 'Ativar'}
                          >
                            {user.is_active ? <PowerOff size={14} /> : <Power size={14} />}
                          </button>
                          
                          <button
                            onClick={() => handleResetPassword(user)}
                            className="btn btn-warning"
                            style={{ padding: '6px 12px' }}
                            title="Resetar Senha"
                          >
                            <Key size={14} />
                          </button>

                          <button
                            onClick={() => setPermissionModal({
                              show: true,
                              user: user,
                              mode: 'user'
                            })}
                            className="btn btn-info"
                            style={{ padding: '6px 12px' }}
                            title="Gerenciar Permissões"
                          >
                            <Shield size={14} />
                          </button>

                          <button
                            onClick={() => handleEdit(user)}
                            className="btn btn-secondary"
                            style={{ padding: '6px 12px' }}
                            title="Editar"
                          >
                            <Edit2 size={14} />
                          </button>
                        </>
                      )}
                      
                      {canDelete && (
                        <button
                          onClick={() => handleDelete(user.id)}
                          className="btn btn-danger"
                          style={{ padding: '6px 12px' }}
                          title="Excluir"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '16px',
        marginTop: '24px'
      }}>
        <div className="card text-center">
          <h4 style={{ margin: '0 0 8px 0', color: 'var(--color-primary)' }}>
            {stats.total}
          </h4>
          <p style={{ margin: 0, fontSize: 'var(--font-size-14)' }}>Total</p>
        </div>
        
        <div className="card text-center">
          <h4 style={{ margin: '0 0 8px 0', color: 'var(--color-green)' }}>
            {stats.active}
          </h4>
          <p style={{ margin: 0, fontSize: 'var(--font-size-14)' }}>Ativos</p>
        </div>
        
        <div className="card text-center">
          <h4 style={{ margin: '0 0 8px 0', color: 'var(--color-red)' }}>
            {stats.inactive}
          </h4>
          <p style={{ margin: 0, fontSize: 'var(--font-size-14)' }}>Inativos</p>
        </div>
        
        <div className="card text-center">
          <h4 style={{ margin: '0 0 8px 0', color: 'var(--color-danger)' }}>
            {stats.admins}
          </h4>
          <p style={{ margin: 0, fontSize: 'var(--font-size-14)' }}>Admins</p>
        </div>
        
        <div className="card text-center">
          <h4 style={{ margin: '0 0 8px 0', color: 'var(--color-warning)' }}>
            {stats.gestors}
          </h4>
          <p style={{ margin: 0, fontSize: 'var(--font-size-14)' }}>Gestors</p>
        </div>
        
        <div className="card text-center">
          <h4 style={{ margin: '0 0 8px 0', color: 'var(--color-info)' }}>
            {stats.emissors}
          </h4>
          <p style={{ margin: 0, fontSize: 'var(--font-size-14)' }}>Emissors</p>
        </div>
        
        <div className="card text-center">
          <h4 style={{ margin: '0 0 8px 0', color: 'var(--color-secondary)' }}>
            {stats.readers}
          </h4>
          <p style={{ margin: 0, fontSize: 'var(--font-size-14)' }}>Leitores</p>
        </div>
      </div>

      {resetPasswordModal.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ width: '400px', maxWidth: '90vw' }}>
            <h3 style={{ margin: '0 0 16px 0' }}>Senha Resetada</h3>
            <p>A senha do usuário <strong>{resetPasswordModal.user?.username}</strong> foi resetada.</p>
            <p>Nova senha temporária:</p>
            <div style={{
              background: '#f8f9fa',
              padding: '12px',
              borderRadius: 'var(--br-5)',
              fontFamily: 'monospace',
              fontSize: '16px',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '16px',
              border: '1px solid var(--color-lightgray)'
            }}>
              {resetPasswordModal.tempPassword}
            </div>
            <p style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>
              O usuário deve alterar esta senha no próximo login.
            </p>
            <button
              onClick={() => setResetPasswordModal({ show: false })}
              className="btn btn-primary"
              style={{ width: '100%' }}
            >
              Fechar
            </button>
          </div>
        </div>
      )}
      {permissionModal.show && (
        <PermissionManager
          user={permissionModal.user}
          mode={permissionModal.mode}
          role={permissionModal.role}
          onClose={() => setPermissionModal({ show: false, mode: 'user' })}
          onSave={() => {
            setPermissionModal({ show: false, mode: 'user' });
            actions.loadUsers();
          }}
        />
      )}
    </div>
  );
};

export default Users;